/** @jsxImportSource @emotion/react */
import { useTheme, Icon, Message, Text } from "basis";
import { getSelfHref, usePersonalLoanAccount } from "library/accounts";
import { useProducts } from "library/hooks/useProducts";
import { getProductName } from "library/transforms";
import { CardButton } from "components/CardButton/CardButton";
import { ProgressCircle } from "../ProgressCircle/ProgressCircle";
import { CardRibbon } from "../CardRibbon/CardRibbon";
import { CurrencyFormat } from "../CurrencyFormat";
import { PAYMENT_LOAN_SUPPORT } from "../../constants/contacts";
import styles from "./personal-loan-card.module.scss";
import { Separator } from "../Separator/Separator";

const PaymentNotReceived = ({ countryCode }) => (
  <Message severity="info-or-minor" bg="transparent" padding="0">
    {`Repayment was not received. Please call ${PAYMENT_LOAN_SUPPORT[countryCode].NUM} for support.`}
  </Message>
);

const ErrorRetrieving = () => (
  <Message severity="blocking" bg="transparent" padding="0" trackId="error">
    Unable to retrieve your account details. Please try again later.
  </Message>
);

const AccountClosed = () => (
  <Message severity="success" bg="transparent" padding="0">
    This account is closed.
  </Message>
);

const FinalPayment = ({ date, countryCode }) => (
  <Text>
    {`Final repayment is due on ${date} of the month. Call ${PAYMENT_LOAN_SUPPORT[countryCode].NUM} to arrange final payment.`}
  </Text>
);

const MinimumPayment = ({ amount, date }) => (
  <Text>
    Minimum repayment of <CurrencyFormat value={amount} /> is due on the {date}{" "}
    of the month.
  </Text>
);

const HoneymoonPeriod = ({ amount, date }) => (
  <Text>
    First repayment of <CurrencyFormat value={amount} /> is due on the {date}.
  </Text>
);

const AccountBalance = ({
  // TODO: change interface from snake_case to camelCase
  loan_balance: loanBalance,
  loan_amount: loanAmount,
  percentage,
  greekable,
}) => {
  const theme = useTheme();

  return (
    <>
      {greekable ? (
        <div className={`${styles.balanceWrapper} greekable`} />
      ) : (
        <div className={styles.balanceWrapper} data-trackid="balance">
          <div>
            <strong>
              <CurrencyFormat value={loanBalance} />
            </strong>{" "}
            outstanding of <CurrencyFormat value={loanAmount} />
          </div>
          <div className={styles.percentageContainer}>
            <ProgressCircle percentage={percentage / 100} />
            <span className={styles.percentageTextContainer}>
              <span className={styles.percentageText}>{`${percentage.toFixed(
                1,
              )}%`}</span>
            </span>
          </div>
        </div>
      )}
      <div css={{ margin: `${theme.space[5]} 0` }}>
        <Separator />
      </div>
    </>
  );
};

function PersonalLoanCard(props) {
  const theme = useTheme();
  const href = getSelfHref(props.account);
  const accountInfo = usePersonalLoanAccount({
    accountId: props.account.account_id,
    enabled: href !== undefined,
    initialData: props.account,
    initialStale: true,
  });
  const account = accountInfo.data ?? {};
  const productsInfo = useProducts();
  const products = productsInfo.data ?? [];
  // TODO: change interface from snake_case to camelCase
  const {
    account_number: accountNumber,
    payment_schedule: paymentSchedule = {},
    loan_amount: loanAmount,
    country_code: countryCode,
    loan_balance: loanBalance = 0,
    is_in_arrears: isInArrears = false,
    honeymoon_period: honeymoonPeriod,
    nextPaymentDay,
    nextPaymentDateLong,
    percentagePaid,
    accountClosed,
    isFinalPayment,
  } = account;
  const greekable =
    !accountInfo.isError && account.greekable ? "greekable" : "";
  const product = products.find((item) => item.id === account.product_id) || {};
  const hideBalance =
    (!loanAmount && !greekable) || accountClosed || accountInfo.isError;

  return (
    <div className={styles.wrapper}>
      {accountClosed && <CardRibbon text="CLOSED" />}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "4px 4px 0px 0px",
          backgroundColor: theme.getColor("primary.blue.t100"),
          height: 108,
          padding: `${theme.space[4]} ${
            accountClosed ? theme.space[14] : theme.space[5]
          } ${theme.space[4]} ${theme.space[5]}`,
        }}
      >
        <Text textStyle="heading5" color="white">
          {getProductName(product)}
        </Text>
        {accountNumber && (
          <Text textStyle="body2" color="white" margin="auto 0 0 0">
            Account number {accountNumber}
          </Text>
        )}
      </div>
      <div className={styles.content}>
        {!hideBalance && (
          <AccountBalance
            greekable={greekable}
            loan_balance={loanBalance}
            loan_amount={loanAmount}
            percentage={percentagePaid}
          />
        )}
        {greekable ? (
          <div className={`${greekable} ${styles.loading}`} />
        ) : accountInfo.isError ? (
          <ErrorRetrieving />
        ) : accountClosed ? (
          <AccountClosed />
        ) : honeymoonPeriod ? (
          <HoneymoonPeriod
            amount={paymentSchedule.monthly_minimum_payment_amount}
            date={nextPaymentDateLong}
          />
        ) : isInArrears ? (
          <PaymentNotReceived countryCode={countryCode} />
        ) : isFinalPayment ? (
          <FinalPayment date={nextPaymentDay} countryCode={countryCode} />
        ) : (
          <MinimumPayment
            amount={paymentSchedule.monthly_minimum_payment_amount}
            date={nextPaymentDay}
          />
        )}
      </div>
      {!greekable && !accountInfo.isError && (
        <CardButton
          href="/personal-loan-account"
          state={{ account }}
          testId="view-account"
          eventData={{
            category: "text-link",
            action: "internal-link",
            label: "View account",
            location: "overview",
            value: account.account_id,
          }}
        >
          <Text textStyle="subtitle2" color="primary.blue.t100">
            <strong>View account</strong>
          </Text>
          <Icon name="chevron-right" color="primary.blue.t100" />
        </CardButton>
      )}
    </div>
  );
}

export { PersonalLoanCard };
